import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'

import {
  createAiDimension,
  deleteAiDimension,
  downloadAiDimensionsWorkbookTemplate,
  estimateAiCost,
  importAiDimensionsWorkbook,
  runAiReport,
} from '../../api_methods'
import { notify } from '../../../utils/index_tagging_tool'
import './AiDimensions.scss'

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Non stimato'
  }
  return `$${Number(value).toFixed(4)}`
}

const formatTimestamp = (value) => {
  if (!value) {
    return 'Mai eseguita'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'Mai eseguita'
  }

  return date.toLocaleString('it-IT')
}

const getModeLabel = (mode) => (mode === 'repeated' ? 'Lista' : 'Normale')

const getSourceLabel = (dimension) => {
  if (dimension.linked_saved_dimension) {
    return 'Dimensione salvata'
  }
  if (dimension.local_tag_count > 0) {
    return 'Manuale / file'
  }
  return 'Da configurare'
}

const isBusyStatus = (status) => status === 'READY_TO_RUN' || status === 'RUNNING'

const buildInitialForm = () => ({
  name: '',
  prompt: '',
  bq_mode: 'nullable',
  active: true,
})

function ConfirmationToggle({ checked, description, id, onChange, title }) {
  return (
    <button
      aria-checked={checked}
      aria-label={title}
      className={`ai-run-toggle ${checked ? 'is-active' : ''}`}
      id={id}
      onClick={() => onChange(!checked)}
      role="switch"
      type="button"
    >
      <span className="ai-run-toggle__copy">
        <span className="ai-run-toggle__title">{title}</span>
        {description ? <span className="ai-run-toggle__description">{description}</span> : null}
      </span>
      <span aria-hidden="true" className="ai-run-toggle__track">
        <span className="ai-run-toggle__thumb" />
      </span>
    </button>
  )
}

function AiDimensionCard({ dimension, reportId, onDelete }) {
  return (
    <Card className="ai-dimension-card shadow-sm">
      <CardBody>
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start">
          <div className="mb-3 mb-lg-0">
            <div className="d-flex align-items-center flex-wrap">
              <h4 className="mb-1 mr-2">{dimension.display_name || dimension.name}</h4>
              <Badge color={dimension.active ? 'success' : 'secondary'} pill>
                {dimension.active ? 'Attiva' : 'Inattiva'}
              </Badge>
            </div>
            <div className="text-muted small">
              Colonna BigQuery: <code>{dimension.bq_column}</code>
            </div>
          </div>
          <div className="d-flex flex-wrap justify-content-lg-end">
            <Link
              className="btn btn-outline-primary btn-sm mr-2 mb-2"
              to={`/reporting/reports/seo/${reportId}/ai-dimensions/${dimension.id}`}
            >
              Apri dettaglio
            </Link>
            <Button
              className="mb-2"
              color="danger"
              outline
              size="sm"
              onClick={() => onDelete(dimension)}
            >
              Elimina
            </Button>
          </div>
        </div>

        <div className="ai-tags-grid mt-3">
          <div className="ai-stat-card">
            <div className="ai-muted-label">Modalita output</div>
            <div>{getModeLabel(dimension.bq_mode)}</div>
          </div>
          <div className="ai-stat-card">
            <div className="ai-muted-label">Sorgente tag</div>
            <div>{getSourceLabel(dimension)}</div>
          </div>
          <div className="ai-stat-card">
            <div className="ai-muted-label">Tag effettivi</div>
            <div>{dimension.effective_tag_count || 0}</div>
          </div>
          <div className="ai-stat-card">
            <div className="ai-muted-label">Ultima stima</div>
            <div>{formatCurrency(dimension.last_estimated_cost)}</div>
          </div>
          <div className="ai-stat-card">
            <div className="ai-muted-label">Ultima esecuzione</div>
            <div>{formatTimestamp(dimension.last_run_at)}</div>
          </div>
        </div>

        {dimension.linked_saved_dimension && (
          <Alert className="mb-0 mt-3" color="light">
            Tassonomia live collegata a <strong>{dimension.linked_saved_dimension.display_name}</strong>.
            I tag locali restano in sola lettura finche il collegamento resta attivo.
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

export default function AiDimensionLinking({ report, aiDimensions, onReload }) {
  const [createForm, setCreateForm] = useState(buildInitialForm())
  const [createErrors, setCreateErrors] = useState({})
  const [creating, setCreating] = useState(false)
  const [estimating, setEstimating] = useState(false)
  const [runLoading, setRunLoading] = useState(false)
  const [reportEstimate, setReportEstimate] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [runPreview, setRunPreview] = useState(null)
  const [confirmQueue, setConfirmQueue] = useState(false)
  const [confirmOverwrite, setConfirmOverwrite] = useState(false)
  const [includeReasoning, setIncludeReasoning] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importMode, setImportMode] = useState('nullable')
  const [importingWorkbook, setImportingWorkbook] = useState(false)

  useEffect(() => {
    setConfirmQueue(false)
    setConfirmOverwrite(false)
  }, [confirmModalOpen])

  const summary = useMemo(() => {
    const activeCount = (aiDimensions || []).filter((dimension) => dimension.active).length
    const totalTags = (aiDimensions || []).reduce(
      (accumulator, dimension) => accumulator + (dimension.effective_tag_count || 0),
      0
    )
    const lastEstimatedTotal = (aiDimensions || []).reduce(
      (accumulator, dimension) => accumulator + Number(dimension.last_estimated_cost || 0),
      0
    )

    return {
      activeCount,
      totalTags,
      lastEstimatedTotal,
    }
  }, [aiDimensions])

  const updateCreateField = (field, value) => {
    setCreateForm((current) => ({
      ...current,
      [field]: value,
    }))
    setCreateErrors((current) => ({
      ...current,
      [field]: null,
    }))
  }

  const loadEstimate = async () => {
    setEstimating(true)
    try {
      const response = await estimateAiCost(report.id, {
        include_reasoning: includeReasoning,
      })
      setReportEstimate(response)
      notify('Stima AI aggiornata.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Impossibile stimare il costo AI del report.', 'error')
    } finally {
      setEstimating(false)
    }
  }

  const handleCreate = async (event) => {
    event.preventDefault()

    const nextErrors = {}
    if (!createForm.name.trim()) {
      nextErrors.name = 'Il nome è obbligatorio.'
    }
    if (!createForm.prompt.trim()) {
      nextErrors.prompt = 'Il prompt è obbligatorio.'
    }

    setCreateErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      return
    }

    setCreating(true)
    try {
      await createAiDimension({
        report: report.id,
        display_name: createForm.name.trim(),
        name: createForm.name.trim(),
        prompt: createForm.prompt.trim(),
        bq_mode: createForm.bq_mode,
        active: createForm.active,
      })
      notify('Dimensione AI creata.', 'success')
      setCreateForm(buildInitialForm())
      await onReload()
    } catch (error) {
      notify(error?.response?.data?.error || 'Errore durante la creazione della dimensione AI.', 'error')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (dimension) => {
    if (!window.confirm(`Eliminare la dimensione AI "${dimension.display_name}"?`)) {
      return
    }

    try {
      await deleteAiDimension(dimension.id)
      notify('Dimensione AI eliminata.', 'success')
      await onReload()
    } catch (error) {
      notify(error?.response?.data?.error || 'Impossibile eliminare la dimensione AI.', 'error')
    }
  }

  const handleRun = async () => {
    setRunLoading(true)
    try {
      const response = await runAiReport(report.id, {
        include_reasoning: includeReasoning,
      })
      if (response?.requires_confirmation) {
        setRunPreview(response)
        setConfirmModalOpen(true)
        return
      }

      notify('Esecuzione AI accodata correttamente.', 'success')
      await onReload()
    } catch (error) {
      notify(error?.response?.data?.error || "Errore durante l'avvio dell esecuzione AI.", "error")
    } finally {
      setRunLoading(false)
    }
  }

  const confirmRun = async () => {
    setRunLoading(true)
    try {
      await runAiReport(report.id, {
        confirmed: true,
        confirm_overwrite: Boolean(runPreview?.overwrite_required),
        include_reasoning: includeReasoning,
      })
      setConfirmModalOpen(false)
      notify('Esecuzione AI accodata correttamente.', 'success')
      await onReload()
    } catch (error) {
      notify(error?.response?.data?.error || 'Errore durante la conferma dell esecuzione AI.', 'error')
    } finally {
      setRunLoading(false)
    }
  }

  const confirmationDisabled =
    !confirmQueue || (Boolean(runPreview?.overwrite_required) && !confirmOverwrite)
  const reportBusy = isBusyStatus(report?.status)

  const handleImportWorkbook = async (event) => {
    event.preventDefault()

    if (!importFile) {
      notify('Seleziona un file Excel da importare.', 'error')
      return
    }

    const formData = new FormData()
    formData.append('report', report.id)
    formData.append('bq_mode', importMode)
    formData.append('file', importFile)

    setImportingWorkbook(true)
    try {
      const response = await importAiDimensionsWorkbook(formData)
      notify(
        `Import completato: ${response?.created_count || 0} dimensioni AI create dal workbook.`,
        'success'
      )
      setImportFile(null)
      await onReload()
    } catch (error) {
      notify(
        error?.response?.data?.error || 'Errore durante l import del workbook AI.',
        'error'
      )
    } finally {
      setImportingWorkbook(false)
    }
  }

  const handleDownloadWorkbookTemplate = async () => {
    try {
      const response = await downloadAiDimensionsWorkbookTemplate()
      const blob = response?.data || response
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'ai_dimensions_template.xlsx'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      notify('Impossibile scaricare il template di esempio.', 'error')
    }
  }

  return (
    <div className="ai-dimensions-workspace Content-box mb-4 mt-3">
      <div className="ai-dimensions-summary">
        <Card className="shadow-sm">
          <CardBody>
            <div className="ai-muted-label">Dimensioni AI attive</div>
            <div className="ai-summary-value">{summary.activeCount}</div>
          </CardBody>
        </Card>
        <Card className="shadow-sm">
          <CardBody>
            <div className="ai-muted-label">Tag effettivi</div>
            <div className="ai-summary-value">{summary.totalTags}</div>
          </CardBody>
        </Card>
        <Card className="shadow-sm">
          <CardBody>
            <div className="ai-muted-label">Ultima stima complessiva</div>
            <div className="ai-summary-value">
              {reportEstimate
                ? formatCurrency(reportEstimate.estimated_cost)
                : formatCurrency(summary.lastEstimatedTotal || null)}
            </div>
          </CardBody>
        </Card>
        <Card className="shadow-sm">
          <CardBody>
            <div className="ai-muted-label">Modalita di esecuzione</div>
            <div className="ai-summary-copy">
              La run standard conserva le colonne AI gia popolate. L'esecuzione solo AI aggiorna
              solo le dimensioni AI.
            </div>
          </CardBody>
        </Card>
      </div>

      <Row className="mt-4">
        <Col xl={8}>
          <Card className="shadow-sm">
            <CardHeader className="border-0">
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center">
                <div>
                  <h3 className="mb-1">Area dimensioni AI</h3>
                  <div className="text-muted small">
                    Crea, stima e lancia le classificazioni AI direttamente dal report.
                  </div>
                  <div className="text-muted small mt-2">
                    {includeReasoning
                      ? 'Motivazioni attive: la run AI salvera anche una colonna dedicata con le spiegazioni.'
                      : 'Motivazioni disattive: la run AI salvera solo i tag classificati.'}
                  </div>
                </div>
                <div className="d-flex flex-wrap mt-3 mt-lg-0">
                  <Button
                    className="mr-2 mb-2"
                    color="primary"
                    outline
                    disabled={estimating || reportBusy}
                    onClick={loadEstimate}
                  >
                    {estimating ? 'Calcolo in corso...' : 'Stima costo report'}
                  </Button>
                  <Button
                    className="mb-2"
                    color="primary"
                    disabled={runLoading || reportBusy}
                    onClick={handleRun}
                  >
                    {runLoading ? 'Avvio...' : 'Esegui solo AI'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="ai-run-modal-section">
                <div className="ai-run-modal-section__header">
                  <h4 className="mb-1">Opzioni output AI</h4>
                  <div className="text-muted small">
                    Attiva le motivazioni solo quando ti servono, per contenere costo e token.
                  </div>
                </div>

                <div className="ai-run-toggle-list">
                  <ConfirmationToggle
                    checked={includeReasoning}
                    description="Per ogni dimensione AI verra salvata anche una colonna companion con le motivazioni."
                    id="ai-run-include-reasoning"
                    onChange={setIncludeReasoning}
                    title="Includi motivazioni nella classificazione AI."
                  />
                </div>
              </div>

              {reportBusy && (
                <Alert color="warning" className="ai-run-warning">
                  Attendi il completamento della run corrente prima di lanciare una nuova esecuzione (sia normale che AI).
                </Alert>
              )}

              {reportEstimate && (
                <Alert color="info" className="ai-run-warning">
                  Stima corrente: <strong>{formatCurrency(reportEstimate.estimated_cost)}</strong> su{' '}
                  <strong>{reportEstimate.keywords}</strong> keyword,{' '}
                  <strong>{reportEstimate.active_dimensions}</strong> dimensioni AI attive è{' '}
                  <strong>{reportEstimate.estimated_calls}</strong> chiamate stimate.
                </Alert>
              )}

              {(!aiDimensions || aiDimensions.length === 0) && (
                <div className="ai-empty-state">
                  <h4 className="mb-2">Nessuna dimensione AI configurata</h4>
                  <p className="text-muted mb-0">
                    Parti creando una dimensione AI, poi completa tassonomia e prompt dal dettaglio.
                  </p>
                </div>
              )}

              {(aiDimensions || []).map((dimension) => (
                <AiDimensionCard
                  key={dimension.id}
                  dimension={dimension}
                  reportId={report.id}
                  onDelete={handleDelete}
                />
              ))}
            </CardBody>
          </Card>
        </Col>

        <Col xl={4} className="mt-4 mt-xl-0">
          <Card className="ai-dimension-form-card shadow-sm">
            <CardHeader className="border-0">
              <h3 className="mb-1">Nuova dimensione AI</h3>
              <div className="text-muted small">
                Crea il contenitore. Tassonomia, collegamento a una dimensione salvata e dettagli avanzati si
                completano nella pagina dedicata.
              </div>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleCreate}>
                <FormGroup>
                  <Label for="ai-dimension-name">Nome</Label>
                  <Input
                    id="ai-dimension-name"
                    invalid={Boolean(createErrors.name)}
                    onChange={(event) => updateCreateField('name', event.target.value)}
                    placeholder="Es. Intento AI"
                    value={createForm.name}
                  />
                  {createErrors.name && <FormFeedback>{createErrors.name}</FormFeedback>}
                </FormGroup>

                <FormGroup>
                  <Label for="ai-dimension-mode">Modalita output</Label>
                  <Input
                    id="ai-dimension-mode"
                    onChange={(event) => updateCreateField('bq_mode', event.target.value)}
                    type="select"
                    value={createForm.bq_mode}
                  >
                    <option value="nullable">Normale</option>
                    <option value="repeated">Lista</option>
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label for="ai-dimension-prompt">Prompt</Label>
                  <Input
                    id="ai-dimension-prompt"
                    invalid={Boolean(createErrors.prompt)}
                    onChange={(event) => updateCreateField('prompt', event.target.value)}
                    rows="6"
                    type="textarea"
                    value={createForm.prompt}
                  />
                  {createErrors.prompt && <FormFeedback>{createErrors.prompt}</FormFeedback>}
                </FormGroup>

                <Button block color="primary" disabled={creating} type="submit">
                  {creating ? 'Creazione...' : 'Crea dimensione AI'}
                </Button>
              </Form>

              <hr className="my-4" />

              <div className="mb-3">
                <h4 className="mb-1">Import da Excel</h4>
                <div className="text-muted small">
                  Carica un file Excel con foglio <strong>Legenda</strong> per i prompt e un foglio per
                  ogni dimensione con i relativi tag.
                </div>
              </div>

              <Form onSubmit={handleImportWorkbook}>
                <FormGroup>
                  <Label for="ai-dimension-import-mode">Modalita output</Label>
                  <Input
                    id="ai-dimension-import-mode"
                    onChange={(event) => setImportMode(event.target.value)}
                    type="select"
                    value={importMode}
                  >
                    <option value="nullable">Normale</option>
                    <option value="repeated">Lista</option>
                  </Input>
                </FormGroup>

                <FormGroup>
                  <div className="d-flex justify-content-between align-items-center">
                    <Label className="mb-0" for="ai-dimension-import-file">
                      File Excel
                    </Label>
                    <button
                      className="btn btn-link btn-sm p-0"
                      onClick={handleDownloadWorkbookTemplate}
                      type="button"
                    >
                      Scarica template di esempio
                    </button>
                  </div>
                  <Input
                    id="ai-dimension-import-file"
                    accept=".xlsx,.xls"
                    onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                    type="file"
                  />
                </FormGroup>

                <Button block color="secondary" disabled={importingWorkbook} type="submit">
                  {importingWorkbook ? 'Import in corso...' : 'Importa workbook AI'}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)}>
        <ModalHeader toggle={() => setConfirmModalOpen(false)}>Conferma esecuzione solo AI</ModalHeader>
        <ModalBody>
          <div className="ai-run-modal-intro">
            <div className="ai-muted-label">Cosa succede adesso</div>
            <p className="mb-0">
              L esecuzione solo AI classifica le keyword con le dimensioni AI attive e aggiorna solo le
              colonne AI del report.
            </p>
          </div>

          <div className="ai-run-modal-section">
            <div className="ai-run-modal-section__header">
              <h4 className="mb-1">Riepilogo run</h4>
              <div className="text-muted small">Controlla costo, volume e dimensione della coda.</div>
            </div>

            <div className="ai-tags-grid">
              <div className="ai-stat-card">
                <div className="ai-muted-label">Costo stimato</div>
                <div>{formatCurrency(runPreview?.estimate?.estimated_cost)}</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-muted-label">Keyword</div>
                <div>{runPreview?.estimate?.keywords || 0}</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-muted-label">Dimensioni AI attive</div>
                <div>{runPreview?.estimate?.active_dimensions || 0}</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-muted-label">Chiamate stimate</div>
                <div>{runPreview?.estimate?.estimated_calls || 0}</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-muted-label">Motivazioni</div>
                <div>{runPreview?.include_reasoning ? 'Attive' : 'Disattive'}</div>
              </div>
            </div>
          </div>

          {runPreview?.overwrite_required && (
            <Alert color="warning" className="ai-run-overwrite-warning mb-0">
              <div className="ai-run-overwrite-warning__title">Sovrascrittura rilevata</div>
              <div>
                In BigQuery risultano gia presenti valori AI nelle colonne:{' '}
                <strong>{(runPreview?.existing_ai_data?.present_columns || []).join(', ')}</strong>.
                La nuova run li sovrascrivera.
              </div>
            </Alert>
          )}

          <div className="ai-run-modal-section">
            <div className="ai-run-modal-section__header">
              <h4 className="mb-1">Conferme richieste</h4>
              <div className="text-muted small">
                Attiva entrambe le conferme richieste per accodare la run.
              </div>
            </div>

            <div className="ai-run-toggle-list">
              <ConfirmationToggle
                checked={confirmQueue}
                description="La classificazione verra accodata in background per questo report."
                id="ai-run-confirm-queue"
                onChange={setConfirmQueue}
                title="Confermo di voler accodare l esecuzione solo AI per questo report."
              />

              {runPreview?.overwrite_required && (
                <ConfirmationToggle
                  checked={confirmOverwrite}
                  description="Le colonne AI gia popolate in BigQuery verranno sostituite con il nuovo output."
                  id="ai-run-confirm-overwrite"
                  onChange={setConfirmOverwrite}
                  title="Confermo la sovrascrittura delle colonne AI gia popolate in BigQuery."
                />
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>
            Annulla
          </Button>
          <Button color="primary" disabled={confirmationDisabled || runLoading} onClick={confirmRun}>
            {runLoading ? 'Conferma in corso...' : 'Conferma esecuzione'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
